import { useEffect, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  useWhatsappConversations,
  whatsappConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import {
  CRMConversationFilter,
  CRMConversationMode,
} from "@jugl-web/rest-api/integrations/types";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { WhatsappConversationTile } from "../WhatsappConversationTile";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";

export interface WhatsappConversationsProps {
  selectedFilter: CRMConversationFilter;
  selectedMode: CRMConversationMode;
  searchQuery: string;
}

export const WhatsappConversations: React.FC<WhatsappConversationsProps> = ({
  selectedFilter,
  selectedMode,
  searchQuery,
}) => {
  const { t } = useTranslations();
  const {
    selectedConversation,
    selectConversation,
    incomingWhatsappMessages$,
  } = useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for WhatsApp based on selected mode
  const computedFilter = useMemo(() => {
    if (selectedFilter !== CRMConversationFilter.active) {
      return selectedFilter;
    }
    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return selectedFilter;
    }
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    reset: resetConversations,
    retry,
    loadMore,
  } = useWhatsappConversations({
    entityId,
    filter: computedFilter,
    searchTerm: searchQuery,
    pageSize: 10,
  });

  useSelectedConversation(conversations);

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingWhatsappMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        // Prepend new conversation to the top
        whatsappConversations$.next({
          type: "prepend",
          // TODO: fix
          conversation: message as unknown as WhatsappConversationModel,
        });
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as WhatsappConversationModel).payload
              .timestamp >= message.payload.timestamp) ||
          message.msg_status === "new"
        ) {
          const updatedConversation = {
            ...selectedConversation,
            ...message,
          };
          whatsappConversations$.next({
            type: "update",
            conversation: updatedConversation as WhatsappConversationModel,
          });
          selectConversation(
            updatedConversation as WhatsappConversationModel,
            true
          );
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.payload.timestamp <= message.payload.timestamp &&
        message.msg_status === "new"
      ) {
        const updatedConversation: WhatsappConversationModel = {
          ...existingConversation,
          ...message,
        };

        if (message.type === "incoming") {
          updatedConversation.since = message.payload.timestamp;
        }
        // Prepend conversation to bump it to the top
        whatsappConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        // Also update selected conversation if needed
        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingWhatsappMessages$,
    conversations,
    selectedConversation,
    selectConversation,
    resetConversations,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  // Empty state content
  const emptyStateContent = useMemo(() => {
    if (conversations.length === 0 && !searchQuery) {
      if (selectedFilter === CRMConversationFilter.active) {
        return (
          <div className="mx-auto mt-40 flex w-full max-w-[340px] flex-col items-center justify-center gap-2 p-5 text-center">
            {(() => {
              if (selectedMode === CRMConversationMode.allChats) {
                return t({
                  id: "client-support-page.you-will-get-notification-once-receive-message",
                  defaultMessage:
                    "You will get a notification once you receive message from Clients 🙌",
                });
              }

              if (selectedMode === CRMConversationMode.onAIAutoReply) {
                return t({
                  id: "client-support-page.no-chats-on-ai-auto-resply",
                  defaultMessage: "No Chats on AI Auto-reply yet  🙌",
                });
              }

              if (selectedMode === CRMConversationMode.waitingResponse) {
                return t({
                  id: "client-support-page.no-chats-waiting-on-response",
                  defaultMessage: "No Chats waiting on response yet  🙌",
                });
              }

              if (selectedMode === CRMConversationMode.managedByMe) {
                return t({
                  id: "client-support-page.no-chats-waiting-on-your-response",
                  defaultMessage: "No Chats waiting on Your response yet  🙌",
                });
              }

              return null;
            })()}
          </div>
        );
      }

      return (
        <div className="mt-6 flex w-full justify-center px-5 text-center">
          <span className="font-secondary text-[15px] leading-4 text-[#828282]">
            {selectedFilter === CRMConversationFilter.expired
              ? t({
                  id: "client-support-page.no-expired-tickets",
                  defaultMessage: "No Expired Tickets 🤩",
                })
              : t({
                  id: "client-support-page.no-blocked-users",
                  defaultMessage: "No Blocked Users 🥳",
                })}
          </span>
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <span className="font-secondary mt-10 text-center text-sm leading-[160%] text-[#828282]">
          {t({
            id: "client-support-page.no-results",
            defaultMessage: "No results 😔",
          })}
        </span>
      );
    }

    return null;
  }, [conversations.length, searchQuery, selectedFilter, selectedMode, t]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <WhatsappConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
          isDraftUnavailable={selectedFilter !== CRMConversationFilter.active}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      {emptyStateContent}
    </DataLoadingWrapper>
  );
};
